export const STUDIO_CANARY_STATES = [
  "preview_only",
  "publish_ready_internal",
  "canary_candidate",
  "canary_blocked",
  "live_blocked",
] as const;

export type StudioCanaryState = (typeof STUDIO_CANARY_STATES)[number];

export type StudioCanaryFlagId =
  | "previewOnly"
  | "internalApprovalRecorded"
  | "launchControlReady"
  | "rolloutManifestPresent"
  | "tenantCanaryAllowed"
  | "canaryEligible"
  | "twilioRoutingUntouched"
  | "promotionBlocked";

export type StudioCanaryFlag = {
  id: StudioCanaryFlagId;
  label: string;
  enabled: boolean;
  detail: string;
};

export type StudioCanaryStateSummary = {
  state: StudioCanaryState;
  stateLabel: string;
  headline: string;
  summary: string;
  flags: StudioCanaryFlag[];
  tenantRestrictions: string[];
  promotionBlocked: boolean;
  promotionNote: string;
};

export type BuildStudioCanaryStateSummaryParams = {
  deploymentState:
    | "draft"
    | "preview_ready"
    | "canary_blocked"
    | "canary_ready"
    | "deploy_blocked";
  launchControlReady: boolean;
  rolloutManifestPresent: boolean;
  rolloutEnabled: boolean;
  canaryEligible: boolean;
  internalApprovalRecorded: boolean;
  approvalStatus?:
    | "pending_owner_approval"
    | "approved_prep_only"
    | "approved_for_a21_canary"
    | null;
  twilioRoutingUntouched?: boolean;
  tenantCanaryAllowed?: boolean;
  tenantScopeLabel?: string;
  blockedReasons?: string[];
};

function buildFlag(
  id: StudioCanaryFlagId,
  label: string,
  enabled: boolean,
  detail: string
): StudioCanaryFlag {
  return {
    id,
    label,
    enabled,
    detail,
  };
}

export function buildStudioCanaryStateSummary(
  params: BuildStudioCanaryStateSummaryParams
): StudioCanaryStateSummary {
  const twilioRoutingUntouched = params.twilioRoutingUntouched ?? true;
  const tenantCanaryAllowed = params.tenantCanaryAllowed ?? false;
  const tenantScopeLabel = params.tenantScopeLabel || "Studio tenant rollout";
  const blockedReasons = params.blockedReasons || [];

   let state: StudioCanaryState = "preview_only";

  if (params.deploymentState === "deploy_blocked") {
    state = "live_blocked";
  } else if (!params.internalApprovalRecorded) {
    state = "preview_only";
  } else if (
    params.rolloutManifestPresent &&
    params.rolloutEnabled &&
    params.canaryEligible &&
    params.launchControlReady &&
    tenantCanaryAllowed &&
    params.approvalStatus === "approved_for_a21_canary"
  ) {
    state = "canary_candidate";
  } else if (
    params.deploymentState === "canary_blocked" ||
    blockedReasons.length > 0 ||
    (params.rolloutEnabled && !params.canaryEligible) ||
    (params.approvalStatus === "approved_for_a21_canary" && !tenantCanaryAllowed)
  ) {
    state = "canary_blocked";
  } else {
    state = "publish_ready_internal";
  }

  const flags: StudioCanaryFlag[] = [
    buildFlag(
      "previewOnly",
      "Preview-only posture",
      state === "preview_only" || state === "publish_ready_internal",
      "Studio remains preview-safe unless a separate rollout workflow explicitly advances later."
    ),
    buildFlag(
      "internalApprovalRecorded",
      "Internal approval recorded",
      params.internalApprovalRecorded,
      "Internal publish sign-off must exist before Studio can frame anything as a canary discussion."
    ),
    buildFlag(
      "launchControlReady",
      "Launch control ready",
      params.launchControlReady,
      "Launch-control blockers remain authoritative for canary discussion."
    ),
    buildFlag(
      "rolloutManifestPresent",
      "Rollout manifest present",
      params.rolloutManifestPresent,
      "A canary candidate must still respect the current rollout manifest and its restrictions."
    ),
    buildFlag(
      "tenantCanaryAllowed",
      "Tenant canary allowed",
      tenantCanaryAllowed,
      "Per-tenant rollout remains externally gated and is not granted automatically from Studio."
    ),
    buildFlag(
      "canaryEligible",
      "Canary eligible",
      params.canaryEligible,
      "Eligibility is descriptive only here. Studio does not execute production promotion."
    ),
    buildFlag(
      "twilioRoutingUntouched",
      "Twilio routing untouched",
      twilioRoutingUntouched,
      "Studio must not mutate Twilio production routing while evaluating canary posture."
    ),
    buildFlag(
      "promotionBlocked",
      "Promotion blocked",
      state !== "canary_candidate",
      "Promotion remains blocked until approval, manifest, tenant restrictions, and launch-control posture all align."
    ),
  ];

  const tenantRestrictions = [
    `${tenantScopeLabel} remains externally gated.`,
    "No public tenant activation is performed from Studio.",
    "White-label showcase readiness does not equal tenant promotion permission.",
  ];

  const promotionBlocked = state !== "canary_candidate";

  const headline =
    state === "preview_only"
      ? "Preview only"
      : state === "publish_ready_internal"
        ? "Publish-ready internal"
        : state === "canary_candidate"
          ? "Canary candidate"
          : state === "canary_blocked"
            ? "Canary blocked"
            : "Live blocked";

  const summary =
    state === "preview_only"
      ? "Studio remains in preview-only posture because internal sign-off has not been recorded yet."
      : state === "publish_ready_internal"
        ? "Internal sign-off exists, but Studio is still preparing operator handoff rather than canary promotion."
        : state === "canary_candidate"
          ? "The current posture is aligned enough for canary discussion, but Studio still does not promote production directly."
          : state === "canary_blocked"
            ? "Internal sign-off exists, but rollout, tenant, or launch-control restrictions still block canary promotion."
            : "A hard deployment or rollout blocker keeps Studio from framing the current state as promotion-ready.";

  const promotionNote =
    state === "canary_candidate"
      ? "Treat this as a canary candidate for operator review only. Promotion still belongs to the separate deployment workflow."
      : state === "publish_ready_internal"
        ? "Internal handoff posture is ready, but canary promotion remains externally gated."
        : state === "canary_blocked"
          ? "Resolve rollout, approval, or tenant restrictions before treating this package as a canary candidate."
          : state === "live_blocked"
            ? "A hard blocker is present. Do not treat this Studio state as rollout-capable."
            : "Keep work inside preview, internal handoff, and operator review until internal sign-off is complete.";

  return {
    state,
    stateLabel: state.replaceAll("_", "-"),
    headline,
    summary,
    flags,
    tenantRestrictions,
    promotionBlocked,
    promotionNote,
  };
}
